import type { ParkingMethodKey, ParkingState } from '../types';

// Split out from the parking engine itself so persistence.ts (and anything
// else needing just the method-key set) doesn't have to import the whole
// ~885-line engine module. In the original index.html this constant was
// defined ~330 lines after its first use in serialize()/deserialize() —
// a forward reference that only worked because both lived in one script
// scope; this module removes that ordering dependency.

// Road-direct is processed first so it can claim road-facing bands before
// back-to-back/tandem pack the remaining area.
export const PARKING_METHOD_KEYS: ParkingMethodKey[] = ['roadDirect', 'backToBack', 'tandem'];

export const PARKING_METHOD_LABELS: Record<ParkingMethodKey, string> = {
  backToBack: '背合わせ',
  tandem: '縦列(タンデム)',
  roadDirect: '道路直接駐車',
};

export const PARKING_METHOD_COLORS: Record<ParkingMethodKey, string> = {
  backToBack: '#4f9d8f',
  tandem: '#c99a3c',
  roadDirect: '#a566c9',
};

const PARKING_ZONE_ID_PREFIX = 'parking-zone-';
export function parkingZoneId(key: ParkingMethodKey): string {
  return PARKING_ZONE_ID_PREFIX + key;
}
export function isParkingZoneId(id: unknown): id is string {
  return typeof id === 'string' && id.indexOf(PARKING_ZONE_ID_PREFIX) === 0;
}
export function parkingZoneKeyFromId(id: string): ParkingMethodKey {
  return id.slice(PARKING_ZONE_ID_PREFIX.length) as ParkingMethodKey;
}

/** Selectable-id for the bicycle zone, alongside the parking-zone ids above (getBuilding/getEditableZones). */
export const BICYCLE_ZONE_ID = 'bicycle-zone';

/** Fixed provision-target ratios (of requiredCars) offered in the UI, e.g. planning for 90% of the legally-derived requirement. */
export const PARKING_TARGET_RATIOS = [1.25, 1.0, 0.9, 0.8, 0.75] as const;

/** requiredCars scaled by targetRatio and rounded up to the nearest car — null when no required count is set. */
export function computeParkingTarget(
  parking: Pick<ParkingState, 'requiredCars' | 'targetRatio'>
): number | null {
  if (parking.requiredCars == null) return null;
  return Math.ceil(parking.requiredCars * parking.targetRatio);
}

/**
 * Add-on parking methods recomputeParking() will auto-enable (never auto-disable) to
 * try to reach the provision target, in priority order. Matches the app's own guidance
 * text: tandem is the first lever for more capacity, road-direct is the narrow-site
 * fallback. backToBack is excluded — it has no on/off toggle, always on.
 */
export const AUTO_FILL_METHOD_ORDER: ParkingMethodKey[] = ['tandem', 'roadDirect'];

/**
 * 縦列(タンデム)・道路直接駐車を使うかどうか。falseの間はサイドバーの項目を表示せず、
 * 確保目標に届かなくても自動で有効化しない(背合わせ・壁背負いのみで配置する)。
 * 戻すときはtrueにするだけでよい(エンジン・UIのコードは残してある)
 */
export const EXTRA_PARKING_METHODS_ENABLED = false;

/** 通路幅(m)。固定値で、画面からは変更できない(保存データの値も読み込み時にこの値へそろえる) */
export const AISLE_WIDTH_FIXED_M = 5.0;

